import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import {
  Autocomplete,
  Alert,
  Box,
  Button,
  CircularProgress,
  Chip,
  FormControl,
  IconButton,
  InputLabel,
  MenuItem,
  Paper,
  Select,
  type SelectChangeEvent,
  Snackbar,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TablePagination,
  TableRow,
  TableSortLabel,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import type { AutocompleteRenderInputParams } from '@mui/material/Autocomplete';
import {
  Add as AddIcon,
  CloudDone as CloudDoneIcon,
  CloudOff as CloudOffIcon,
  FileDownload as FileDownloadIcon,
  Image as ImageIcon,
  LocalPrintshop as PrintIcon,
  QrCodeScanner as QrCodeScannerIcon,
  Visibility as VisibilityIcon,
} from '@mui/icons-material';
import {
  exportAdminInventoryCsv,
  fetchAdminInventoryList,
  fetchInventoryBrands,
  fetchInventoryCategories,
  fetchInventoryConditionsByCategory,
  searchInventoryProducts,
  fetchInventorySizesByCategory,
  searchInventoryConsignors,
  searchInventoryModels,
  reprintInventoryLabel,
  fetchInventorySelection,
} from '../api/inventoryAdmin';
import type {
  InventoryConsignorOption,
  InventoryFilterOption,
  InventoryModelOption,
  InventoryProductSearchOption,
  InventorySelectionItem,
} from '../api/inventoryAdmin';
import { InventoryCheckDialog } from './InventoryCheckDialog';
import { QRLookupDialog } from './QRLookupDialog';
import type {
  AdminInventoryOrderBy,
  AdminInventoryOrderDirection,
  AdminInventoryRow,
  InventoryStatus,
} from '../types/inventory';
import {
  DEFAULT_ADMIN_INVENTORY_ORDER_BY,
  DEFAULT_ADMIN_INVENTORY_ORDER_DIRECTION,
  INVENTORY_STATUS_OPTIONS,
} from '../types/inventory';
import InventoryStatusChip from './InventoryStatusChip';
import AdminCreateProductModal from './AdminCreateProductModal';
import AdminEditInventoryVariantModal from './AdminEditInventoryVariantModal';
import { ADMIN_INVENTORY_ADD_PATH } from '../routes/routePaths';

const CARD_BORDER = '1px solid';
const CARD_BORDER_COLOR = 'rgba(226, 232, 240, 1)';
const TABLE_HEADER_BG = 'rgba(248, 250, 252, 1)';

const moneyMx = (n: number) =>
  new Intl.NumberFormat('es-MX', { style: 'currency', currency: 'MXN' }).format(n);

function normalizeLabel(v: string | null | undefined): string {
  return String(v ?? '')
    .trim()
    .toLowerCase();
}

/** Sufijo "cm" solo si la talla es numérica; letras (p. ej. M, L) se muestran tal cual. */
function formatInventorySize(sizeCm: string | null): string {
  if (sizeCm == null || !String(sizeCm).trim()) {
    return 'Sin talla';
  }
  const t = String(sizeCm).trim();
  if (/^-?\d+([.,]\d+)?$/.test(t)) {
    return `${t} cm`;
  }
  return t;
}

const defaultFilters = {
  name: '',
  productId: null as number | null,
  categoryId: null as number | null,
  brandId: null as number | null,
  modelId: null as number | null,
  sizeId: null as number | null,
  conditionId: null as number | null,
  consignorId: null as number | null,
  sku: '',
  status: '' as '' | InventoryStatus,
};

type FiltersState = typeof defaultFilters;

function inventoryStatusFromSearchParam(raw: string | null): '' | InventoryStatus {
  if (!raw || !(INVENTORY_STATUS_OPTIONS as readonly string[]).includes(raw)) {
    return '';
  }
  return raw as InventoryStatus;
}

const sortLabelSx = {
  '& .MuiTableSortLabel-icon': { opacity: 0.45 },
  '&.Mui-active .MuiTableSortLabel-icon': { opacity: 1 },
};

function summaryFromPageItems(items: AdminInventoryRow[]): {
  totalCost: number;
  totalPrice: number;
  availableCount: number;
  totalCount: number;
} {
  return items.reduce(
    (acc, row) => ({
      totalCost: acc.totalCost + row.cost,
      totalPrice: acc.totalPrice + row.price,
      availableCount: acc.availableCount + (row.status === 'Activo' ? 1 : 0),
      totalCount: acc.totalCount + 1,
    }),
    { totalCost: 0, totalPrice: 0, availableCount: 0, totalCount: 0 }
  );
}

function SummaryCard({
  title,
  value,
  subtitle,
}: {
  title: string;
  value: string;
  subtitle: string;
}): React.ReactElement {
  return (
    <Paper
      elevation={0}
      sx={{
        p: 2,
        border: CARD_BORDER,
        borderColor: CARD_BORDER_COLOR,
        borderRadius: 2,
      }}
    >
      <Typography
        variant="caption"
        sx={{ fontWeight: 700, color: 'text.secondary', textTransform: 'uppercase' }}
      >
        {title}
      </Typography>
      <Typography variant="h5" sx={{ fontWeight: 700, mt: 0.5 }}>
        {value}
      </Typography>
      <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 1 }}>
        {subtitle}
      </Typography>
    </Paper>
  );
}

function renderFilterAutocompleteInput(
  params: AutocompleteRenderInputParams,
  cfg: { label: string; placeholder: string; loading?: boolean; helperText?: string }
): React.ReactElement {
  return (
    <TextField
      id={params.id}
      disabled={params.disabled}
      fullWidth={params.fullWidth}
      size={params.size}
      label={cfg.label}
      placeholder={cfg.placeholder}
      helperText={cfg.helperText}
      slotProps={{
        inputLabel: params.InputLabelProps,
        htmlInput: params.inputProps,
        input: {
          ref: params.InputProps.ref,
          className: params.InputProps.className,
          startAdornment: params.InputProps.startAdornment,
          endAdornment: (
            <>
              {cfg.loading ? <CircularProgress color="inherit" size={16} /> : null}
              {params.InputProps.endAdornment}
            </>
          ),
        },
      }}
    />
  );
}

/**
 * Listado de inventario para administración. Contrato del API: `src/api/inventoryAdmin.ts`.
 */
function AdminInventoryPage(): React.ReactElement {
  const [searchParams, setSearchParams] = useSearchParams();
  const consignorIdRaw = searchParams.get('consignorId');
  const lockedConsignorId = useMemo(() => {
    const n = Number(consignorIdRaw);
    return Number.isFinite(n) && n > 0 ? n : undefined;
  }, [consignorIdRaw]);

  const statusFromUrl = searchParams.get('status');

  const [draftFilters, setDraftFilters] = useState<FiltersState>(() => ({
    ...defaultFilters,
    status: inventoryStatusFromSearchParam(searchParams.get('status')),
  }));
  const [committedFilters, setCommittedFilters] = useState<FiltersState>(() => ({
    ...defaultFilters,
    status: inventoryStatusFromSearchParam(searchParams.get('status')),
  }));
  const [orderBy, setOrderBy] = useState<AdminInventoryOrderBy>(DEFAULT_ADMIN_INVENTORY_ORDER_BY);
  const [orderDirection, setOrderDirection] = useState<AdminInventoryOrderDirection>(
    DEFAULT_ADMIN_INVENTORY_ORDER_DIRECTION
  );
  const [page, setPage] = useState(0);
  const [pageSize, setPageSize] = useState(25);
  const [rows, setRows] = useState<AdminInventoryRow[]>([]);
  const [total, setTotal] = useState(0);
  const [summary, setSummary] = useState<ReturnType<typeof summaryFromPageItems> | null>(null);
  const [summaryFromApi, setSummaryFromApi] = useState(false);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [exportLoading, setExportLoading] = useState(false);
  const [snackbar, setSnackbar] = useState<{ open: boolean; message: string }>({
    open: false,
    message: '',
  });
  const [categories, setCategories] = useState<InventoryFilterOption[]>([]);
  const [brands, setBrands] = useState<InventoryFilterOption[]>([]);
  const [sizes, setSizes] = useState<InventoryFilterOption[]>([]);
  const [conditions, setConditions] = useState<InventoryFilterOption[]>([]);
  const [models, setModels] = useState<InventoryModelOption[]>([]);
  const [consignorOptions, setConsignorOptions] = useState<InventoryConsignorOption[]>([]);
  const [loadingCategoryDeps, setLoadingCategoryDeps] = useState(false);
  const [loadingModels, setLoadingModels] = useState(false);
  const [loadingConsignors, setLoadingConsignors] = useState(false);
  const [modelSearchInput, setModelSearchInput] = useState('');
  const [consignorSearchInput, setConsignorSearchInput] = useState('');
  const [productSearchInput, setProductSearchInput] = useState('');
  const [productOptions, setProductOptions] = useState<InventoryProductSearchOption[]>([]);
  const [loadingProducts, setLoadingProducts] = useState(false);
  const [selectedProductOption, setSelectedProductOption] =
    useState<InventoryProductSearchOption | null>(null);
  const [productEditModalOpen, setProductEditModalOpen] = useState(false);
  const [variantEditModalOpen, setVariantEditModalOpen] = useState(false);
  const [editInventoryId, setEditInventoryId] = useState<number | null>(null);

  // Open variant modal directly when navigated here from a QR scan on the home page
  const qrVariantIdRaw = searchParams.get('qrVariantId');
  useEffect(() => {
    if (!qrVariantIdRaw) return;
    const id = Number(qrVariantIdRaw);
    if (Number.isFinite(id) && id > 0) {
      setEditInventoryId(id);
      setVariantEditModalOpen(true);
      // Remove the param so back/refresh doesn't reopen the modal
      setSearchParams(
        (prev) => {
          const next = new URLSearchParams(prev);
          next.delete('qrVariantId');
          return next;
        },
        { replace: true }
      );
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [qrVariantIdRaw]);

  const productLocksCatalogFilters = Boolean(
    selectedProductOption && draftFilters.productId != null
  );
  const draftCategoryId = draftFilters.categoryId;
  const draftBrandId = draftFilters.brandId;

  useEffect(() => {
    let cancelled = false;
    (async () => {
      const [categoryItems, brandItems] = await Promise.all([
        fetchInventoryCategories(),
        fetchInventoryBrands(),
      ]);
      if (cancelled) return;
      setCategories(categoryItems);
      setBrands(brandItems);
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  useEffect(() => {
    let cancelled = false;
    const categoryId = draftCategoryId;
    if (!categoryId) {
      setSizes([]);
      setConditions([]);
      setDraftFilters((prev) => ({ ...prev, sizeId: null, conditionId: null }));
      setLoadingCategoryDeps(false);
    } else {
      setLoadingCategoryDeps(true);
      (async () => {
        const [sizeItems, conditionItems] = await Promise.all([
          fetchInventorySizesByCategory(categoryId),
          fetchInventoryConditionsByCategory(categoryId),
        ]);
        if (cancelled) return;
        setSizes(sizeItems);
        setConditions(conditionItems);
        setDraftFilters((prev) => ({
          ...prev,
          sizeId: sizeItems.some((x) => x.id === prev.sizeId) ? prev.sizeId : null,
          conditionId: conditionItems.some((x) => x.id === prev.conditionId)
            ? prev.conditionId
            : null,
        }));
        setLoadingCategoryDeps(false);
      })();
    }
    return () => {
      cancelled = true;
    };
  }, [draftCategoryId]);

  useEffect(() => {
    let cancelled = false;
    const q = modelSearchInput.trim();
    let timer: ReturnType<typeof setTimeout> | null = null;
    setLoadingModels(true);
    timer = setTimeout(async () => {
      const items = await searchInventoryModels({
        q,
        categoryId: draftCategoryId ?? undefined,
        brandId: draftBrandId ?? undefined,
      });
      if (cancelled) return;
      setModels(items);
      setLoadingModels(false);
    }, 300);
    return () => {
      cancelled = true;
      if (timer) clearTimeout(timer);
    };
  }, [modelSearchInput, draftCategoryId, draftBrandId]);

  useEffect(() => {
    let cancelled = false;
    const q = consignorSearchInput.trim();
    let timer: ReturnType<typeof setTimeout> | null = null;
    if (lockedConsignorId !== undefined) {
      setConsignorOptions([]);
      setConsignorSearchInput('');
      setLoadingConsignors(false);
    } else {
      setLoadingConsignors(true);
      timer = setTimeout(async () => {
        const items = await searchInventoryConsignors(q);
        if (cancelled) return;
        setConsignorOptions(items);
        setLoadingConsignors(false);
      }, 300);
    }
    return () => {
      cancelled = true;
      if (timer) clearTimeout(timer);
    };
  }, [consignorSearchInput, lockedConsignorId]);

  useEffect(() => {
    let cancelled = false;
    const q = productSearchInput.trim();
    let timer: ReturnType<typeof setTimeout> | null = null;
    if (!q) {
      setProductOptions([]);
      setLoadingProducts(false);
    } else {
      setLoadingProducts(true);
      timer = setTimeout(async () => {
        const items = await searchInventoryProducts(q);
        if (cancelled) return;
        setProductOptions(items);
        setLoadingProducts(false);
      }, 300);
    }
    return () => {
      cancelled = true;
      if (timer) clearTimeout(timer);
    };
  }, [productSearchInput]);

  useEffect(() => {
    if (!selectedProductOption || draftFilters.productId == null) return;
    setDraftFilters((prev) => {
      let next = prev;
      let changed = false;
      if (next.categoryId == null && selectedProductOption.category) {
        const resolvedCategoryId = categories.find(
          (x) => normalizeLabel(x.label) === normalizeLabel(selectedProductOption.category)
        )?.id;
        if (resolvedCategoryId) {
          next = { ...next, categoryId: resolvedCategoryId };
          changed = true;
        }
      }
      if (next.brandId == null && selectedProductOption.brand) {
        const resolvedBrandId = brands.find(
          (x) => normalizeLabel(x.label) === normalizeLabel(selectedProductOption.brand)
        )?.id;
        if (resolvedBrandId) {
          next = { ...next, brandId: resolvedBrandId };
          changed = true;
        }
      }
      return changed ? next : prev;
    });
  }, [selectedProductOption, draftFilters.productId, categories, brands]);

  /** Sincroniza filtro Estado con `?status=` (deep links y menú “Recogida pendiente”). */
  useEffect(() => {
    const normalized = inventoryStatusFromSearchParam(statusFromUrl);
    setDraftFilters((prev) =>
      prev.status === normalized ? prev : { ...prev, status: normalized }
    );
    setCommittedFilters((prev) =>
      prev.status === normalized ? prev : { ...prev, status: normalized }
    );
    setPage(0);
  }, [statusFromUrl]);

  const buildListQuery = useCallback(() => {
    return {
      page: page + 1,
      pageSize,
      orderBy,
      orderDirection,
      name: committedFilters.name.trim() || undefined,
      categoryId: committedFilters.categoryId ?? undefined,
      brandId: committedFilters.brandId ?? undefined,
      modelId: committedFilters.modelId ?? undefined,
      sizeId: committedFilters.sizeId ?? undefined,
      conditionId: committedFilters.conditionId ?? undefined,
      productId: committedFilters.productId ?? undefined,
      consignorId: lockedConsignorId ?? committedFilters.consignorId ?? undefined,
      sku: committedFilters.sku.trim() || undefined,
      status: committedFilters.status || undefined,
    };
  }, [page, pageSize, orderBy, orderDirection, committedFilters, lockedConsignorId]);

  const handleRequestSort = useCallback(
    (field: AdminInventoryOrderBy) => {
      setPage(0);
      if (orderBy === field) {
        setOrderDirection((d) => (d === 'asc' ? 'desc' : 'asc'));
      } else {
        setOrderBy(field);
        setOrderDirection('desc');
      }
    },
    [orderBy]
  );

  const load = useCallback(async () => {
    setLoading(true);
    setLoadError(null);
    const result = await fetchAdminInventoryList(buildListQuery());
    setLoading(false);
    if (!result.ok) {
      setLoadError(result.error);
      setRows([]);
      setTotal(0);
      setSummary(null);
      setSummaryFromApi(false);
      return;
    }
    const { data } = result;
    setRows(data.items);
    setTotal(data.total);
    if (data.summary) {
      setSummary({
        totalCost: data.summary.totalCost,
        totalPrice: data.summary.totalPrice,
        availableCount: data.summary.availableCount,
        totalCount: data.summary.totalCount,
      });
      setSummaryFromApi(true);
    } else {
      setSummary(summaryFromPageItems(data.items));
      setSummaryFromApi(false);
    }
  }, [buildListQuery]);

  useEffect(() => {
    load().catch(() => {});
  }, [load]);

  const handleApplyFilters = () => {
    setCommittedFilters({ ...draftFilters });
    setPage(0);
    setSearchParams((prev) => {
      const next = new URLSearchParams(prev);
      const st = draftFilters.status;
      if (st) {
        next.set('status', st);
      } else {
        next.delete('status');
      }
      return next;
    });
  };

  const handleClearFilters = () => {
    const cleared = { ...defaultFilters };
    setDraftFilters(cleared);
    setCommittedFilters(cleared);
    setModelSearchInput('');
    setConsignorSearchInput('');
    setProductSearchInput('');
    setSelectedProductOption(null);
    setProductOptions([]);
    setOrderBy(DEFAULT_ADMIN_INVENTORY_ORDER_BY);
    setOrderDirection(DEFAULT_ADMIN_INVENTORY_ORDER_DIRECTION);
    setPage(0);
    setPageSize(25);
    setSearchParams((prev) => {
      const next = new URLSearchParams(prev);
      next.delete('consignorId');
      next.delete('status');
      return next;
    });
  };

  const handleClearConsignorLock = () => {
    setSearchParams((prev) => {
      const next = new URLSearchParams(prev);
      next.delete('consignorId');
      return next;
    });
    setPage(0);
  };

  const handleConsignorNavigate = (consignorId: number) => {
    const cleared = { ...defaultFilters };
    setDraftFilters(cleared);
    setCommittedFilters(cleared);
    setModelSearchInput('');
    setConsignorSearchInput('');
    setSearchParams(new URLSearchParams({ consignorId: String(consignorId) }));
    setPage(0);
  };

  const openProductEdit = (row: AdminInventoryRow) => {
    setEditInventoryId(row.id);
    setProductEditModalOpen(true);
  };

  const openVariantEdit = (row: AdminInventoryRow) => {
    setEditInventoryId(row.id);
    setVariantEditModalOpen(true);
  };

  // ── Reprint ──────────────────────────────────────────────────────────────
  const [reprintingId, setReprintingId] = useState<number | null>(null);

  const handleReprint = async (row: AdminInventoryRow) => {
    setReprintingId(row.id);
    const result = await reprintInventoryLabel(row.id);
    setReprintingId(null);
    if (result.ok) {
      setSnackbar({ open: true, message: 'Etiqueta enviada a la cola de impresión ✓' });
    } else {
      setSnackbar({ open: true, message: result.error });
    }
  };

  // ── QR lookup (scan → open variant popup) ────────────────────────────────
  const [qrLookupOpen, setQrLookupOpen] = useState(false);

  const handleQRFound = (variantId: number) => {
    setEditInventoryId(variantId);
    setVariantEditModalOpen(true);
  };

  // ── Inventariar selección ─────────────────────────────────────────────────
  const [inventoryCheckOpen, setInventoryCheckOpen] = useState(false);
  const [inventoryCheckItems, setInventoryCheckItems] = useState<InventorySelectionItem[]>([]);
  const [inventoryCheckLoading, setInventoryCheckLoading] = useState(false);

  const handleOpenInventoryCheck = async () => {
    setInventoryCheckLoading(true);
    const {
      categoryId,
      brandId,
      modelId,
      sizeId,
      conditionId,
      productId,
      consignorId,
      name,
      sku,
      status,
    } = committedFilters;
    const result = await fetchInventorySelection({
      orderBy: 'id',
      orderDirection: 'asc',
      name: name || undefined,
      categoryId: categoryId ?? undefined,
      brandId: brandId ?? undefined,
      modelId: modelId ?? undefined,
      sizeId: sizeId ?? undefined,
      conditionId: conditionId ?? undefined,
      productId: productId ?? undefined,
      consignorId: consignorId ?? undefined,
      sku: sku || undefined,
      status: status || undefined,
    });
    setInventoryCheckLoading(false);
    if (result.ok) {
      setInventoryCheckItems(result.data);
      setInventoryCheckOpen(true);
    } else {
      setSnackbar({ open: true, message: result.error });
    }
  };

  const closeProductEditModal = () => {
    setProductEditModalOpen(false);
    setEditInventoryId(null);
  };

  const closeVariantEditModal = () => {
    setVariantEditModalOpen(false);
    setEditInventoryId(null);
  };

  const handleProductUpdated = () => {
    closeProductEditModal();
    setSnackbar({ open: true, message: 'Producto actualizado correctamente.' });
    load().catch(() => {});
  };

  const handleVariantSaved = () => {
    closeVariantEditModal();
    setSnackbar({ open: true, message: 'Producto actualizado correctamente.' });
    load().catch(() => {});
  };

  const handleExport = async () => {
    setExportLoading(true);
    const q = buildListQuery();
    const { page: _p, pageSize: _s, ...filters } = q;
    const result = await exportAdminInventoryCsv(filters);
    setExportLoading(false);
    if (!result.ok) {
      setSnackbar({ open: true, message: result.error });
    }
  };

  const handleChangePage = (_: unknown, newPage: number) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (e: React.ChangeEvent<HTMLInputElement>) => {
    setPageSize(parseInt(e.target.value, 10));
    setPage(0);
  };

  const updateDraft = <K extends keyof FiltersState>(key: K, value: FiltersState[K]) => {
    setDraftFilters((prev) => ({ ...prev, [key]: value }));
  };

  const onStatusChange = (e: SelectChangeEvent<string>) => {
    const v = e.target.value as '' | InventoryStatus;
    setDraftFilters((prev) => ({ ...prev, status: v }));
  };

  const rangeLabel =
    total === 0 ? '0' : `${page * pageSize + 1}–${Math.min((page + 1) * pageSize, total)}`;

  return (
    <Box sx={{ maxWidth: 1400, mx: 'auto' }}>
      <Box
        sx={{
          display: 'flex',
          flexDirection: { xs: 'column', sm: 'row' },
          alignItems: { sm: 'center' },
          justifyContent: 'space-between',
          gap: 2,
          mb: 3,
        }}
      >
        <Typography variant="h4" sx={{ fontWeight: 700, letterSpacing: '-0.02em' }}>
          Inventario
        </Typography>
        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1.5 }}>
          <Button
            variant="outlined"
            startIcon={<FileDownloadIcon />}
            onClick={() => {
              handleExport().catch(() => {});
            }}
            disabled={exportLoading}
            sx={{
              borderColor: CARD_BORDER_COLOR,
              color: 'text.primary',
              fontWeight: 600,
              textTransform: 'none',
            }}
          >
            {exportLoading ? 'Exportando…' : 'Descargar CSV'}
          </Button>
          <Tooltip title="Escanear etiqueta QR para ver detalles">
            <IconButton
              onClick={() => setQrLookupOpen(true)}
              sx={{
                border: CARD_BORDER,
                borderColor: CARD_BORDER_COLOR,
                borderRadius: 1.5,
                p: 1,
              }}
              aria-label="Escanear QR"
            >
              <QrCodeScannerIcon />
            </IconButton>
          </Tooltip>
          <Button
            variant="outlined"
            startIcon={
              inventoryCheckLoading ? <CircularProgress size={16} /> : <QrCodeScannerIcon />
            }
            onClick={() => {
              handleOpenInventoryCheck().catch(() => {});
            }}
            disabled={inventoryCheckLoading}
            sx={{
              borderColor: CARD_BORDER_COLOR,
              color: 'text.primary',
              fontWeight: 600,
              textTransform: 'none',
            }}
          >
            Inventariar selección
          </Button>
          <Button
            variant="contained"
            component={Link}
            to={ADMIN_INVENTORY_ADD_PATH}
            startIcon={<AddIcon />}
            sx={{
              bgcolor: '#000',
              fontWeight: 600,
              textTransform: 'none',
              boxShadow: '0 1px 2px rgba(0,0,0,0.08)',
              '&:hover': { bgcolor: '#1e293b' },
            }}
          >
            Agregar producto
          </Button>
        </Box>
      </Box>

      <Paper
        elevation={0}
        sx={{
          border: CARD_BORDER,
          borderColor: CARD_BORDER_COLOR,
          borderRadius: 2,
          overflow: 'hidden',
          mb: 3,
        }}
      >
        <Box
          sx={{
            p: 2,
            borderBottom: CARD_BORDER,
            borderColor: CARD_BORDER_COLOR,
            display: 'flex',
            flexDirection: 'column',
            gap: 2,
          }}
        >
          <Typography variant="subtitle2" sx={{ fontWeight: 700, color: 'text.secondary' }}>
            Filtros avanzados
          </Typography>
          {lockedConsignorId !== undefined && (
            <Box>
              <Chip
                label={`Consignador n.º ${lockedConsignorId}`}
                onDelete={handleClearConsignorLock}
                color="primary"
                variant="outlined"
                sx={{ fontWeight: 600 }}
              />
            </Box>
          )}
          <Box
            sx={{
              display: 'grid',
              gridTemplateColumns: { xs: '1fr', sm: 'repeat(2, 1fr)', lg: 'repeat(4, 1fr)' },
              gap: 2,
            }}
          >
            <Autocomplete<InventoryProductSearchOption, false, false, true>
              size="small"
              options={productOptions}
              value={selectedProductOption}
              inputValue={productSearchInput}
              freeSolo
              loading={loadingProducts}
              getOptionLabel={(o) => (typeof o === 'string' ? o : o.title)}
              isOptionEqualToValue={(a, b) => a.id === b.id}
              onInputChange={(_, value, reason) => {
                if (reason === 'input') {
                  setProductSearchInput(value);
                  setSelectedProductOption(null);
                  setDraftFilters((prev) => ({
                    ...prev,
                    ...defaultFilters,
                    name: value,
                    productId: null,
                  }));
                } else if (reason === 'clear') {
                  setProductSearchInput('');
                  setSelectedProductOption(null);
                  setProductOptions([]);
                  updateDraft('name', '');
                  updateDraft('productId', null);
                }
              }}
              onChange={(_, value) => {
                if (typeof value === 'string') {
                  setProductSearchInput(value);
                  setSelectedProductOption(null);
                  setDraftFilters((prev) => ({
                    ...prev,
                    ...defaultFilters,
                    name: value,
                    productId: null,
                  }));
                  return;
                }
                if (!value) {
                  setProductSearchInput('');
                  setSelectedProductOption(null);
                  setProductOptions([]);
                  updateDraft('name', '');
                  updateDraft('productId', null);
                  return;
                }
                setSelectedProductOption(value);
                setProductSearchInput(value.title);
                setProductOptions((prev) =>
                  prev.some((x) => x.id === value.id) ? prev : [...prev, value]
                );
                const resolvedCategoryId =
                  value.categoryId ??
                  categories.find((x) => normalizeLabel(x.label) === normalizeLabel(value.category))
                    ?.id ??
                  null;
                const resolvedBrandId =
                  value.brandId ??
                  brands.find((x) => normalizeLabel(x.label) === normalizeLabel(value.brand))?.id ??
                  null;
                const resolvedModelId = value.modelId ?? null;
                setModelSearchInput(value.model ?? '');
                if (resolvedModelId && value.model) {
                  setModels((prev) =>
                    prev.some((x) => x.id === resolvedModelId)
                      ? prev
                      : [...prev, { id: resolvedModelId, label: value.model as string }]
                  );
                }
                setConsignorSearchInput('');
                setDraftFilters({
                  ...defaultFilters,
                  name: value.title,
                  productId: value.id,
                  categoryId: resolvedCategoryId,
                  brandId: resolvedBrandId,
                  modelId: resolvedModelId,
                });
              }}
              noOptionsText={
                productSearchInput.trim() ? 'Sin coincidencias' : 'Escribe para buscar'
              }
              renderInput={(params) =>
                renderFilterAutocompleteInput(params, {
                  label: 'Nombre del producto',
                  placeholder: 'Buscar por título…',
                  loading: loadingProducts,
                })
              }
              renderOption={(optionProps, option) =>
                React.createElement(
                  'li',
                  optionProps,
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.25, minWidth: 0 }}>
                    <Box
                      sx={{
                        width: 36,
                        height: 36,
                        borderRadius: 1,
                        bgcolor: 'action.hover',
                        border: CARD_BORDER,
                        borderColor: CARD_BORDER_COLOR,
                        overflow: 'hidden',
                        flexShrink: 0,
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                      }}
                    >
                      {option.thumbnailUrl ? (
                        <Box
                          component="img"
                          src={option.thumbnailUrl}
                          alt={option.title ? `Foto: ${option.title}` : 'Imagen del producto'}
                          sx={{ width: '100%', height: '100%', objectFit: 'cover' }}
                        />
                      ) : (
                        <ImageIcon sx={{ color: 'action.disabled', fontSize: 18 }} />
                      )}
                    </Box>
                    <Box sx={{ textAlign: 'left', minWidth: 0, flex: 1 }}>
                      <Typography variant="body2" sx={{ fontWeight: 600 }} noWrap>
                        {option.title || 'Sin título'}
                      </Typography>
                      <Typography variant="caption" color="text.secondary" noWrap>
                        {[option.brand, option.category].filter(Boolean).join(' · ') ||
                          'Sin metadata'}
                      </Typography>
                    </Box>
                  </Box>
                )
              }
            />
            <FormControl size="small" sx={{ minWidth: 180 }}>
              <InputLabel id="inv-category-label">Categoría</InputLabel>
              <Select
                labelId="inv-category-label"
                label="Categoría"
                value={draftFilters.categoryId == null ? '' : String(draftFilters.categoryId)}
                disabled={productLocksCatalogFilters}
                onChange={(e) => {
                  const { value } = e.target;
                  const nextCategoryId = value === '' ? null : Number(value);
                  setDraftFilters((prev) => ({
                    ...prev,
                    categoryId: Number.isFinite(nextCategoryId) ? nextCategoryId : null,
                    sizeId: null,
                    conditionId: null,
                    modelId: null,
                  }));
                  setModelSearchInput('');
                  setModels([]);
                }}
              >
                <MenuItem value="">
                  <em>Todas</em>
                </MenuItem>
                {categories.map((c) => (
                  <MenuItem key={c.id} value={String(c.id)}>
                    {c.label}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
            <Autocomplete<InventoryFilterOption, false, false, false>
              size="small"
              options={brands}
              value={brands.find((b) => b.id === draftFilters.brandId) ?? null}
              disabled={productLocksCatalogFilters}
              onChange={(_, value) => {
                setDraftFilters((prev) => ({
                  ...prev,
                  brandId: value?.id ?? null,
                  modelId: null,
                }));
                setModelSearchInput('');
                setModels([]);
              }}
              getOptionLabel={(o) => o.label}
              isOptionEqualToValue={(a, b) => a.id === b.id}
              noOptionsText="Sin marcas"
              openOnFocus
              renderInput={(params) =>
                renderFilterAutocompleteInput(params, {
                  label: 'Marca',
                  placeholder: 'Selecciona marca',
                })
              }
            />
            <Autocomplete<InventoryModelOption, false, false, false>
              size="small"
              options={models}
              value={models.find((m) => m.id === draftFilters.modelId) ?? null}
              inputValue={modelSearchInput}
              disabled={productLocksCatalogFilters}
              onInputChange={(_, value, reason) => {
                setModelSearchInput(value);
                if (reason === 'clear') {
                  updateDraft('modelId', null);
                }
              }}
              onChange={(_, value) => {
                updateDraft('modelId', value?.id ?? null);
                if (value) {
                  setModelSearchInput(value.label);
                  setModels((prev) =>
                    prev.some((x) => x.id === value.id) ? prev : [...prev, value]
                  );
                }
              }}
              loading={loadingModels}
              getOptionLabel={(o) => o.label}
              isOptionEqualToValue={(a, b) => a.id === b.id}
              noOptionsText={modelSearchInput.trim() ? 'Sin coincidencias' : 'Sin resultados'}
              openOnFocus
              renderInput={(params) =>
                renderFilterAutocompleteInput(params, {
                  label: 'Modelo',
                  placeholder: 'Buscar modelo…',
                  loading: loadingModels,
                })
              }
            />
            <Autocomplete<InventoryFilterOption, false, false, false>
              size="small"
              options={sizes}
              value={sizes.find((s) => s.id === draftFilters.sizeId) ?? null}
              onChange={(_, value) => updateDraft('sizeId', value?.id ?? null)}
              disabled={!draftFilters.categoryId || loadingCategoryDeps}
              loading={loadingCategoryDeps}
              getOptionLabel={(o) => o.label}
              isOptionEqualToValue={(a, b) => a.id === b.id}
              noOptionsText={draftFilters.categoryId ? 'Sin tallas' : 'Selecciona categoría'}
              renderInput={(params) =>
                renderFilterAutocompleteInput(params, {
                  label: 'Talla',
                  placeholder: 'Selecciona talla',
                  loading: loadingCategoryDeps,
                })
              }
            />
            <Autocomplete<InventoryFilterOption, false, false, false>
              size="small"
              options={conditions}
              value={conditions.find((c) => c.id === draftFilters.conditionId) ?? null}
              onChange={(_, value) => updateDraft('conditionId', value?.id ?? null)}
              disabled={!draftFilters.categoryId || loadingCategoryDeps}
              loading={loadingCategoryDeps}
              getOptionLabel={(o) => o.label}
              isOptionEqualToValue={(a, b) => a.id === b.id}
              noOptionsText={draftFilters.categoryId ? 'Sin condiciones' : 'Selecciona categoría'}
              renderInput={(params) =>
                renderFilterAutocompleteInput(params, {
                  label: 'Condición',
                  placeholder: 'Selecciona condición',
                  loading: loadingCategoryDeps,
                })
              }
            />
            <Autocomplete<InventoryConsignorOption, false, false, false>
              size="small"
              options={consignorOptions}
              value={consignorOptions.find((c) => c.id === draftFilters.consignorId) ?? null}
              inputValue={consignorSearchInput}
              onInputChange={(_, value, reason) => {
                setConsignorSearchInput(value);
                if (reason === 'clear') {
                  updateDraft('consignorId', null);
                }
              }}
              onChange={(_, value) => {
                updateDraft('consignorId', value?.id ?? null);
                if (value) {
                  setConsignorSearchInput(value.fullName);
                  setConsignorOptions((prev) =>
                    prev.some((x) => x.id === value.id) ? prev : [...prev, value]
                  );
                }
              }}
              loading={loadingConsignors}
              getOptionLabel={(o) => o.fullName}
              isOptionEqualToValue={(a, b) => a.id === b.id}
              noOptionsText={
                consignorSearchInput.trim() ? 'Sin coincidencias' : 'Escribe para buscar'
              }
              openOnFocus
              disabled={lockedConsignorId !== undefined}
              renderInput={(params) =>
                renderFilterAutocompleteInput(params, {
                  label: 'Consignador',
                  placeholder: 'Buscar consignador…',
                  loading: loadingConsignors,
                  helperText:
                    lockedConsignorId !== undefined
                      ? 'Quita el filtro por consignador para habilitar esta búsqueda.'
                      : undefined,
                })
              }
            />
            <TextField
              size="small"
              label="Código SKU o UU ID"
              value={draftFilters.sku}
              onChange={(e) => updateDraft('sku', e.target.value)}
              InputProps={{ sx: { fontFamily: 'ui-monospace, monospace' } }}
            />
            <FormControl size="small" sx={{ minWidth: 200 }}>
              <InputLabel id="inv-status-label">Estado</InputLabel>
              <Select
                labelId="inv-status-label"
                label="Estado"
                value={draftFilters.status}
                onChange={onStatusChange}
                sx={{
                  '& .MuiSelect-select': {
                    textAlign: 'left',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'flex-start',
                  },
                }}
              >
                <MenuItem value="">
                  <em>Todos</em>
                </MenuItem>
                {INVENTORY_STATUS_OPTIONS.map((s) => (
                  <MenuItem key={s} value={s}>
                    {s}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
            <Box
              sx={{
                display: 'flex',
                flexWrap: 'wrap',
                alignItems: 'flex-start',
                gap: 1,
                mt: 0.5,
              }}
            >
              <Button
                variant="contained"
                onClick={handleApplyFilters}
                sx={{
                  bgcolor: '#000',
                  textTransform: 'none',
                  fontWeight: 600,
                  px: 3,
                  '&:hover': { bgcolor: '#1e293b' },
                }}
              >
                Aplicar filtros
              </Button>
              <Button
                variant="outlined"
                onClick={handleClearFilters}
                sx={{
                  borderColor: CARD_BORDER_COLOR,
                  color: 'text.primary',
                  textTransform: 'none',
                  fontWeight: 600,
                  px: 3,
                }}
              >
                Limpiar filtros
              </Button>
            </Box>
          </Box>
          <Typography variant="caption" color="text.secondary">
            Mostrando {rangeLabel} de {total} productos
          </Typography>
        </Box>

        {loadError && (
          <Alert severity="warning" sx={{ m: 2, mb: 0 }}>
            {loadError}
          </Alert>
        )}

        <TableContainer sx={{ maxWidth: '100%', overflowX: 'auto' }}>
          <Table size="small" sx={{ minWidth: 1200 }}>
            <TableHead>
              <TableRow sx={{ bgcolor: TABLE_HEADER_BG }}>
                <TableCell
                  sortDirection={orderBy === 'id' ? orderDirection : false}
                  sx={{ fontSize: 11, fontWeight: 700, textTransform: 'uppercase' }}
                >
                  <TableSortLabel
                    active={orderBy === 'id'}
                    direction={orderBy === 'id' ? orderDirection : 'asc'}
                    onClick={() => handleRequestSort('id')}
                    sx={sortLabelSx}
                  >
                    Id.
                  </TableSortLabel>
                </TableCell>
                <TableCell sx={{ fontSize: 11, fontWeight: 700, textTransform: 'uppercase' }}>
                  UU ID
                </TableCell>
                <TableCell
                  sortDirection={orderBy === 'title' ? orderDirection : false}
                  sx={{ fontSize: 11, fontWeight: 700, textTransform: 'uppercase' }}
                >
                  <TableSortLabel
                    active={orderBy === 'title'}
                    direction={orderBy === 'title' ? orderDirection : 'asc'}
                    onClick={() => handleRequestSort('title')}
                    sx={sortLabelSx}
                  >
                    Producto
                  </TableSortLabel>
                </TableCell>
                <TableCell sx={{ fontSize: 11, fontWeight: 700, textTransform: 'uppercase' }}>
                  Código SKU
                </TableCell>
                <TableCell
                  align="left"
                  sx={{ fontSize: 11, fontWeight: 700, textTransform: 'uppercase', width: 180 }}
                >
                  Consignador
                </TableCell>
                <TableCell
                  sortDirection={orderBy === 'size' ? orderDirection : false}
                  sx={{ fontSize: 11, fontWeight: 700, textTransform: 'uppercase' }}
                >
                  <TableSortLabel
                    active={orderBy === 'size'}
                    direction={orderBy === 'size' ? orderDirection : 'asc'}
                    onClick={() => handleRequestSort('size')}
                    sx={sortLabelSx}
                  >
                    Talla (cm)
                  </TableSortLabel>
                </TableCell>
                <TableCell sx={{ fontSize: 11, fontWeight: 700, textTransform: 'uppercase' }}>
                  Condición
                </TableCell>
                <TableCell
                  sortDirection={orderBy === 'cost' ? orderDirection : false}
                  sx={{ fontSize: 11, fontWeight: 700, textTransform: 'uppercase' }}
                >
                  <TableSortLabel
                    active={orderBy === 'cost'}
                    direction={orderBy === 'cost' ? orderDirection : 'asc'}
                    onClick={() => handleRequestSort('cost')}
                    sx={sortLabelSx}
                  >
                    Costo (pago)
                  </TableSortLabel>
                </TableCell>
                <TableCell
                  sortDirection={orderBy === 'price' ? orderDirection : false}
                  sx={{ fontSize: 11, fontWeight: 700, textTransform: 'uppercase' }}
                >
                  <TableSortLabel
                    active={orderBy === 'price'}
                    direction={orderBy === 'price' ? orderDirection : 'asc'}
                    onClick={() => handleRequestSort('price')}
                    sx={sortLabelSx}
                  >
                    Precio
                  </TableSortLabel>
                </TableCell>
                <TableCell sx={{ fontSize: 11, fontWeight: 700, textTransform: 'uppercase' }}>
                  Estado
                </TableCell>
                <TableCell
                  align="right"
                  sx={{ fontSize: 11, fontWeight: 700, textTransform: 'uppercase' }}
                >
                  Acciones
                </TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {!loading &&
                rows.map((row) => (
                  <TableRow
                    key={row.id}
                    hover
                    sx={{ '&:last-child td': { borderBottom: 0 }, fontSize: 14 }}
                  >
                    <TableCell sx={{ color: 'text.secondary' }}>{row.id}</TableCell>
                    <TableCell
                      sx={{
                        fontFamily: 'ui-monospace, monospace',
                        fontSize: 12,
                        color: row.artId ? 'text.secondary' : 'text.disabled',
                        fontStyle: row.artId ? 'inherit' : 'italic',
                      }}
                    >
                      {row.artId ?? 'Sin UU ID'}
                    </TableCell>
                    <TableCell>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                        <Box
                          sx={{
                            width: 48,
                            height: 48,
                            borderRadius: 1,
                            bgcolor: 'action.hover',
                            border: CARD_BORDER,
                            borderColor: CARD_BORDER_COLOR,
                            overflow: 'hidden',
                            flexShrink: 0,
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                          }}
                        >
                          {row.imageUrl ? (
                            <Box
                              component="img"
                              src={row.imageUrl}
                              alt={row.title ? `Foto: ${row.title}` : 'Imagen del producto'}
                              sx={{ width: '100%', height: '100%', objectFit: 'cover' }}
                            />
                          ) : (
                            <ImageIcon sx={{ color: 'action.disabled' }} />
                          )}
                        </Box>
                        <Box sx={{ minWidth: 0 }}>
                          <Typography
                            component="button"
                            type="button"
                            onClick={() => openProductEdit(row)}
                            sx={{
                              display: 'block',
                              border: 'none',
                              background: 'none',
                              p: 0,
                              m: 0,
                              font: 'inherit',
                              fontWeight: 700,
                              textAlign: 'left',
                              cursor: 'pointer',
                              color: 'text.primary',
                              maxWidth: 220,
                              overflow: 'hidden',
                              textOverflow: 'ellipsis',
                              whiteSpace: 'nowrap',
                              '&:hover': { textDecoration: 'underline' },
                            }}
                          >
                            {row.title || 'Sin título'}
                          </Typography>
                          <Typography variant="caption" color="text.secondary" display="block">
                            {row.category ?? 'Sin categoría'}
                          </Typography>
                        </Box>
                      </Box>
                    </TableCell>
                    <TableCell
                      sx={{
                        fontFamily: 'ui-monospace, monospace',
                        fontSize: 12,
                        color: row.sku ? 'text.secondary' : 'text.disabled',
                        fontStyle: row.sku ? 'inherit' : 'italic',
                      }}
                    >
                      {row.sku ?? 'Sin SKU'}
                    </TableCell>
                    <TableCell
                      align="left"
                      sx={{
                        verticalAlign: 'middle',
                        width: 180,
                        maxWidth: 200,
                        py: 1.5,
                      }}
                    >
                      <Typography
                        component="button"
                        type="button"
                        variant="body2"
                        onClick={() => handleConsignorNavigate(row.consignorId)}
                        title={row.consignorName || undefined}
                        sx={{
                          display: 'block',
                          width: '100%',
                          border: 'none',
                          background: 'none',
                          p: 0,
                          m: 0,
                          font: 'inherit',
                          fontWeight: 600,
                          textAlign: 'left',
                          cursor: 'pointer',
                          color: 'text.primary',
                          lineHeight: 1.35,
                          overflow: 'hidden',
                          textOverflow: 'ellipsis',
                          whiteSpace: 'nowrap',
                          '&:hover': { textDecoration: 'underline' },
                        }}
                      >
                        {row.consignorName || 'Sin consignador'}
                      </Typography>
                    </TableCell>
                    <TableCell>{formatInventorySize(row.sizeCm)}</TableCell>
                    <TableCell>
                      <Chip
                        label={row.condition}
                        size="small"
                        sx={{
                          height: 22,
                          fontSize: 10,
                          fontWeight: 700,
                          bgcolor: 'action.hover',
                          borderRadius: '9999px',
                        }}
                      />
                    </TableCell>
                    <TableCell sx={{ color: 'text.secondary' }}>{moneyMx(row.cost)}</TableCell>
                    <TableCell sx={{ fontWeight: 700 }}>{moneyMx(row.price)}</TableCell>
                    <TableCell>
                      <Box
                        sx={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-start' }}
                      >
                        <InventoryStatusChip status={row.status} />
                        <Chip
                          size="small"
                          clickable={Boolean(row.shopifyActive && row.handle)}
                          component={row.shopifyActive && row.handle ? 'a' : 'div'}
                          href={
                            row.shopifyActive && row.handle
                              ? `https://urbanutility.mx/products/${encodeURIComponent(row.handle)}`
                              : undefined
                          }
                          target={row.shopifyActive && row.handle ? '_blank' : undefined}
                          rel={row.shopifyActive && row.handle ? 'noopener noreferrer' : undefined}
                          icon={
                            row.shopifyActive ? (
                              <CloudDoneIcon sx={{ fontSize: 14 }} />
                            ) : (
                              <CloudOffIcon sx={{ fontSize: 14 }} />
                            )
                          }
                          label="Shopify"
                          variant={row.shopifyActive ? 'filled' : 'outlined'}
                          sx={{
                            mt: 0.75,
                            height: 20,
                            fontSize: 10,
                            fontWeight: 700,
                            borderRadius: '9999px',
                            bgcolor: row.shopifyActive ? 'rgba(34, 197, 94, 0.12)' : undefined,
                            color: row.shopifyActive ? 'rgb(21, 128, 61)' : 'text.secondary',
                            borderColor: row.shopifyActive ? 'rgb(21, 128, 61)' : 'divider',
                            '& .MuiChip-icon': {
                              color: row.shopifyActive ? 'rgb(21, 128, 61)' : 'text.secondary',
                              ml: 0.5,
                            },
                            '&:hover':
                              row.shopifyActive && row.handle
                                ? {
                                    boxShadow: '0 0 0 1px rgb(21, 128, 61) inset',
                                    textDecoration: 'none',
                                  }
                                : undefined,
                          }}
                        />
                      </Box>
                    </TableCell>
                    <TableCell align="right" sx={{ whiteSpace: 'nowrap' }}>
                      <Tooltip title="Reimprimir etiqueta">
                        <span>
                          <IconButton
                            size="small"
                            aria-label="Reimprimir etiqueta"
                            onClick={() => {
                              handleReprint(row).catch(() => {});
                            }}
                            disabled={reprintingId === row.id}
                          >
                            {reprintingId === row.id ? (
                              <CircularProgress size={16} />
                            ) : (
                              <PrintIcon fontSize="small" />
                            )}
                          </IconButton>
                        </span>
                      </Tooltip>
                      <Tooltip title="Ver detalles">
                        <IconButton
                          size="small"
                          aria-label="Ver detalles del producto"
                          onClick={() => openVariantEdit(row)}
                        >
                          <VisibilityIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                    </TableCell>
                  </TableRow>
                ))}
              {loading && (
                <TableRow>
                  <TableCell colSpan={11} align="center" sx={{ py: 6 }}>
                    <Typography color="text.secondary">Cargando…</Typography>
                  </TableCell>
                </TableRow>
              )}
              {!loading && rows.length === 0 && (
                <TableRow>
                  <TableCell colSpan={11} align="center" sx={{ py: 6 }}>
                    <Typography color="text.secondary">
                      No hay productos con estos filtros.
                    </Typography>
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </TableContainer>
        <TablePagination
          component="div"
          count={total}
          page={page}
          onPageChange={handleChangePage}
          rowsPerPage={pageSize}
          onRowsPerPageChange={handleChangeRowsPerPage}
          rowsPerPageOptions={[10, 25, 50]}
          labelRowsPerPage="Filas por página"
          labelDisplayedRows={({ from, to, count }) =>
            `${from}–${to} de ${count !== -1 ? count : `más de ${to}`}`
          }
        />
      </Paper>

      {summary && (
        <Box
          sx={{
            display: 'grid',
            gridTemplateColumns: { xs: '1fr', sm: 'repeat(2, 1fr)', lg: 'repeat(4, 1fr)' },
            gap: 2,
          }}
        >
          <SummaryCard
            title="Costo total (pago a consignadores)"
            value={moneyMx(summary.totalCost)}
            subtitle={
              summaryFromApi
                ? 'Según filtros (todos los resultados)'
                : 'Solo los productos de esta página'
            }
          />
          <SummaryCard
            title="Precio de venta total"
            value={moneyMx(summary.totalPrice)}
            subtitle={
              summaryFromApi
                ? 'Según filtros (todos los resultados)'
                : 'Solo los productos de esta página'
            }
          />
          <SummaryCard
            title="Productos activos"
            value={String(summary.availableCount)}
            subtitle={
              summaryFromApi ? 'En estado Activo, según filtros' : 'En estado Activo en esta página'
            }
          />
          <SummaryCard
            title="Total de registros"
            value={String(summary.totalCount)}
            subtitle={summaryFromApi ? 'Coinciden con los filtros' : 'En esta página'}
          />
        </Box>
      )}

      <Snackbar
        open={snackbar.open}
        autoHideDuration={4000}
        onClose={() => setSnackbar((s) => ({ ...s, open: false }))}
        message={snackbar.message}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      />
      <AdminCreateProductModal
        open={productEditModalOpen}
        mode="edit"
        editInventoryId={editInventoryId}
        onClose={closeProductEditModal}
        onUpdated={handleProductUpdated}
      />
      <AdminEditInventoryVariantModal
        open={variantEditModalOpen}
        inventoryId={editInventoryId}
        onClose={closeVariantEditModal}
        onSaved={handleVariantSaved}
      />
      <QRLookupDialog
        open={qrLookupOpen}
        onClose={() => setQrLookupOpen(false)}
        onFound={handleQRFound}
      />
      <InventoryCheckDialog
        open={inventoryCheckOpen}
        items={inventoryCheckItems}
        onClose={() => setInventoryCheckOpen(false)}
      />
    </Box>
  );
}

export default AdminInventoryPage;
